"use client"

import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle, DialogTrigger } from "../ui/dialog";
import { useState } from "react";
import { deploymentAvailaility } from "@/services/deployment.service";
import { Input } from "../ui/input";
import { z } from "zod";
import { UpdateStatus } from "@/constants/status";
import { FtpFormSchema, FtpSchema } from "@/schemas/ftp.schema";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { editConnection } from "@/services/ftpConnections.service";
import { ToggleInput, ToggleLabel, ToggleSpan } from "../ui/toggle";

interface Props {
    connection: z.infer<typeof FtpSchema>,
    updateData: () => void
}

export default function FtpEditModal(props: Props) {
    const [open, setOpen] = useState(false)
    const [updateStatus, setUpdateStatus] = useState(UpdateStatus.standBy);
    const [failedMessage, setFailedMessage] = useState("");
    const [connectionApproved, setConnectionApproved] = useState(props.connection.CB_approved == 1 ? true : false);

    const form = useForm<z.infer<typeof FtpFormSchema>>({
        resolver: zodResolver(FtpFormSchema),
        defaultValues: {
          Name: props.connection.Name,
          Server: props.connection.Server,
          Username: props.connection.Username,
          Password: ""
        },
    });

    async function EditConnection(data: z.infer<typeof FtpFormSchema>){
        setUpdateStatus(UpdateStatus.onGoing);

        const authCheck = await deploymentAvailaility();
        if(authCheck === false){
            console.log("Auth Failed");
            setUpdateStatus(UpdateStatus.notAuthroized);
            return;
        }

        const formData = new FormData();
        formData.append('id', props.connection.ID);
        formData.append('name', data!.Name);
        formData.append('server', data!.Server);
        formData.append('username', data!.Username);
        formData.append('password', data!.Password);
        formData.append('cbApproved', connectionApproved.toString());

        const status = await editConnection(formData);
        if(status != "Connection edited"){
            console.log(status);
            setFailedMessage("Connection editing failed. Check console.log for more information.<br>Otherwise, here: ".concat(status));
            setUpdateStatus(UpdateStatus.failed);
            return;
        }

        handleSuccess();
        return;
    }

    function errorHandler(data: unknown){
        console.log("Error Occoured:")
        console.log(data);
    }

    const handleSuccess = () => {
        setUpdateStatus(UpdateStatus.completed);
        form.reset();
        setTimeout(() => {
            props.updateData();
            setUpdateStatus(UpdateStatus.standBy);
            setOpen(false);
        }, 3000);
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="cursor-pointer">Edit</Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
                <DialogTitle>Edit FTP Connection</DialogTitle>
                <DialogDescription> 
                </DialogDescription>
                <div>
                    {(updateStatus === UpdateStatus.standBy ||
                        updateStatus === UpdateStatus.notAuthroized) && (
                            <Form {...form}>
                                <form 
                                    autoComplete="off"
                                    className="grid gap-y-4"
                                    onSubmit={form.handleSubmit(EditConnection, errorHandler)}
                                >
                                    <div className="grid grid-cols-4 gap-4">
                                        <div className="col-start-1 col-end-4 rounded-md border-black">
                                            <FormField
                                                control={form.control}
                                                name="Name"
                                                render={({ field }) => (
                                                    <FormItem className="mb-2">
                                                        <FormLabel>Name</FormLabel>
                                                        <FormControl>
                                                            <Input
                                                            placeholder="Name"
                                                            {...field}
                                                            />
                                                        </FormControl>

                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name="Server"
                                                render={({ field }) => (
                                                    <FormItem className="mb-2">
                                                        <FormLabel>Server</FormLabel>
                                                        <FormControl>
                                                            <Input
                                                            placeholder="Server"
                                                            {...field}
                                                            />
                                                        </FormControl>

                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name="Username"
                                                render={({ field }) => (
                                                    <FormItem className="mb-2">
                                                        <FormLabel>Username</FormLabel>
                                                        <FormControl>
                                                            <Input
                                                            placeholder="Username"
                                                            {...field}
                                                            />
                                                        </FormControl>

                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name="Password"
                                                render={({ field }) => (
                                                    <FormItem className="mb-2">
                                                        <FormLabel>Password</FormLabel>
                                                        <FormControl>
                                                            <Input
                                                            placeholder="Password"
                                                            {...field}
                                                            />
                                                        </FormControl>

                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                        <div className="col-start-4 col-end-4 rounded-md border-black">
                                            <ToggleLabel>
                                                Clearbook Approved?
                                                <ToggleInput checked={connectionApproved} onChange={() => {
                                                        setConnectionApproved(!connectionApproved);
                                                    }}/>
                                                <ToggleSpan/>
                                            </ToggleLabel>
                                        </div>
                                    </div>
                                    <div className="col-start-1 col-end-7">
                                        {updateStatus == UpdateStatus.notAuthroized && (
                                        <h1 className="w-full text-xl font-bold text-black dark:text-white mb-2 py-1 px-2 rounded-md bg-red-500">
                                            Authorization to deploy failed
                                        </h1>
                                        )}
                                        <Button
                                            className="w-full mt-4 cursor-pointer"
                                            type="submit"
                                        >
                                            Edit Connection
                                        </Button>
                                    </div>
                                </form>
                            </Form>
                    )}
                </div>
                <DialogFooter>
                    <div className="grid grid-cols-6 gap-4">
                        <div className="col-start-1 col-end-7 rounded-md border-black">
                            {updateStatus == UpdateStatus.onGoing && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>Editing Entry...</p>
                                </div>
                            )}
                            {updateStatus == UpdateStatus.completed && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>Edited Entry! Will refresh this page momentarily</p>
                                </div>
                            )}
                            {updateStatus == UpdateStatus.failed && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>{failedMessage}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}